import type { CSSProperties, ReactNode } from "react";

interface SafeRenderProps {
  value: unknown;
  fallback?: ReactNode;
  asObject?: "verbose" | "compact" | "hidden";
}

const serif: CSSProperties = {
  fontFamily: '"Playfair Display", serif',
  lineHeight: 1.6,
};

export function SafeRender({
  value,
  fallback = null,
  asObject = "compact",
}: SafeRenderProps) {
  if (value === null || value === undefined) return <>{fallback}</>;

  if (typeof value === "string" || typeof value === "number") {
    return <>{value}</>;
  }

  if (typeof value === "boolean") {
    return <>{value ? "Oui" : "Non"}</>;
  }

  if (Array.isArray(value)) {
    return (
      <>
        {value.map((item, i) => (
          <SafeRender key={i} value={item} fallback={fallback} asObject={asObject} />
        ))}
      </>
    );
  }

  if (typeof value === "object") {
    if (asObject === "hidden") return <>{fallback}</>;

    const v = value as Record<string, unknown>;
    if ("synthese" in v || "key_points" in v || "divergences" in v) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {typeof v.synthese === "string" && v.synthese.trim() && (
            <p style={{ ...serif, margin: 0, fontWeight: 600 }}>{v.synthese}</p>
          )}
          {Array.isArray(v.key_points) && v.key_points.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 20, ...serif, fontSize: 14 }}>
              {v.key_points.map((kp, i) => (
                <li key={i}>
                  <SafeRender value={kp} fallback={fallback} asObject={asObject} />
                </li>
              ))}
            </ul>
          )}
          {Array.isArray(v.divergences) && v.divergences.length > 0 && (
            <div>
              <p
                style={{
                  margin: "10px 0 6px",
                  fontFamily: '"DM Mono", monospace',
                  fontSize: 10,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: "var(--leather)",
                  fontWeight: 600,
                }}
              >
                Divergences
              </p>
              <ul style={{ margin: 0, paddingLeft: 20, ...serif, fontSize: 14 }}>
                {v.divergences.map((d, i) => (
                  <li key={i}>
                    <SafeRender value={d} fallback={fallback} asObject={asObject} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    if (asObject === "verbose") {
      return (
        <pre
          style={{
            margin: 0,
            whiteSpace: "pre-wrap",
            fontFamily: '"DM Mono", monospace',
            fontSize: 11,
            lineHeight: 1.5,
            color: "var(--smoke)",
          }}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    return <>{fallback}</>;
  }

  return <>{fallback}</>;
}

export function safeText(value: unknown, fallback = ""): string {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return fallback;
}

export default SafeRender;